import { createFileRoute, Link } from "@tanstack/react-router";
import { Button, Flex, Typography } from "antd";
import { useEffect, useRef } from "react";
import { useLinkAWSMarketplaceSubscription } from "@/common/artemis-operations/organization";
import { ERROR_NETWORK_DESC } from "@/common/messages";
import { AuthPageLayout } from "@/components/auth-layout";
import { FullSpin } from "@/components/base";

export const Route = createFileRoute("/_authenticated/aws-mktp")({
	validateSearch: (
		search: Record<string, unknown>,
	): { token?: string; error?: string } => ({
		token: typeof search.token === "string" ? search.token : undefined,
		error: typeof search.error === "string" ? search.error : undefined,
	}),
	component: AWSMarketplaceLanding,
});

/**
 * Landing page for AWS Marketplace buyers. The API's fulfillment endpoint redirects here with
 * `?token=<claim token>` (or `?error=…`). Sitting under `_authenticated`, the login round-trip and
 * new-org onboarding happen first; this page then links the subscription to the caller's org.
 */
function AWSMarketplaceLanding() {
	const { token, error } = Route.useSearch();
	const link = useLinkAWSMarketplaceSubscription();
	const sent = useRef(false);

	useEffect(() => {
		// Guard against StrictMode's double effect so the token is redeemed once.
		if (token && !sent.current) {
			sent.current = true;
			link.mutate(token);
		}
	}, [token, link]);

	if (token && (link.isIdle || link.isPending)) {
		return (
			<div style={{ height: "100vh" }}>
				<FullSpin size="large" description="Linking your AWS subscription…" />
			</div>
		);
	}

	const ok = link.isSuccess;
	const message = ok
		? "Your AWS Marketplace subscription is now linked to your organization, and its plan limits have been applied."
		: link.isError
			? link.error instanceof Error
				? link.error.message
				: ERROR_NETWORK_DESC
			: error === "missing_token" || !token
				? "No AWS Marketplace subscription was found in this link."
				: "AWS Marketplace could not confirm this subscription. Open the product from the AWS Marketplace console again.";

	return (
		<AuthPageLayout>
			<Flex vertical align="center" gap={16}>
				<Typography.Title level={3} style={{ margin: 0, textAlign: "center" }}>
					{ok ? "Subscription linked" : "Couldn't link your subscription"}
				</Typography.Title>
				<Typography.Text type="secondary" style={{ textAlign: "center" }}>
					{message}
				</Typography.Text>
				<Link to="/home" style={{ width: "100%" }}>
					<Button type="primary" size="large" block>
						Continue to StratoNext
					</Button>
				</Link>
			</Flex>
		</AuthPageLayout>
	);
}
